import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Network, Send, Loader, Lightbulb, Plus, ChevronRight, AlertTriangle, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const TEMPLATES_FALLBACK = [
  { id: 'order', title: 'Order-to-Delivery', prompt: '' },
  { id: 'invoice', title: 'Invoice-to-Payment', prompt: '' },
  { id: 'hiring', title: 'Hiring Process', prompt: '' },
  { id: 'support', title: 'Customer Support', prompt: '' },
];

const ProcessMapper = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.mapper;
  const chatEndRef = useRef(null);
  const [view, setView] = useState('start');
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [processText, setProcessText] = useState('');
  const [mapData, setMapData] = useState(null);
  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Process Mapper', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  React.useEffect(() => {
    axios.get(`${API}/compass/mapper/templates`).then(r => setTemplates(r.data.templates)).catch(() => setTemplates(TEMPLATES_FALLBACK));
  }, []);

  const mapProcess = async (text) => {
    if (!((text) == null ? '' : String(text)).trim()) return;
    setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/mapper/map`, { process_description: text, language: lang }, { headers });
      setSessionId(res.data.session_id); setMapData(res.data.map_data);
      setMessages([{ role: 'user', content: text }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Mapping failed. Please check your API configuration.' : 'Mapping mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]); setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/mapper/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setProcessText(''); setMapData(null); };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><Network size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="mapper-start">
            <div className="auto-input-card">
              <div className="auto-input-header"><Network size={20} /><h3 className="heading-3">{tp.describeProcess}</h3></div>
              <p className="body-small text-secondary">{lang === 'nl' ? 'Beschrijf een workflow en AI maakt een map, detecteert knelpunten en suggereert verbeteringen.' : 'Describe a workflow and AI will map it, detect bottlenecks, and suggest improvements.'}</p>
              <textarea className="form-input auto-textarea" placeholder={tp.placeholder} value={processText} onChange={(e) => setProcessText(e.target.value)} rows={5} data-testid="mapper-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={() => mapProcess(processText)} disabled={!((processText) == null ? '' : String(processText)).trim()} data-testid="mapper-map-btn">
                <Network size={16} /> {tp.mapBtn}
              </button>
            </div>
            <div data-testid="mapper-templates">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{lang === 'nl' ? 'Of gebruik een template' : 'Or use a template'}</h3>
              <div className="auto-template-grid">
                {(Array.isArray(templates) ? templates : []).map(tmpl => (
                  <button key={tmpl.id} className="auto-template-card" onClick={() => { setProcessText(tmpl.prompt); mapProcess(tmpl.prompt); }} data-testid={`mapper-tpl-${tmpl.id}`}>
                    <div className="auto-template-top"><Network size={18} /><ChevronRight size={16} className="compass-role-arrow" /></div>
                    <h4 className="body-medium" style={{ fontWeight: 600 }}>{tmpl.title}</h4>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="mapper-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-blue-200)' }}><Network size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        {mapData && (
          <div className="sim-sidebar-stats" data-testid="mapper-stats">
            {mapData.optimization_score != null && <div className="sim-stat-item"><CheckCircle size={14} /><span className="body-small">{lang === 'nl' ? 'Optimalisatie' : 'Optimization'}</span><span className="body-small" style={{ fontWeight: 600 }}>{mapData.optimization_score}/100</span></div>}
            {mapData.bottlenecks && <div className="sim-stat-item" style={{ background: mapData.bottlenecks.length > 0 ? 'var(--accent-orange-200)' : 'var(--accent-green-200)' }}><AlertTriangle size={14} /><span className="body-small">{lang === 'nl' ? 'Knelpunten' : 'Bottlenecks'}</span><span className="body-small" style={{ fontWeight: 600 }}>{mapData.bottlenecks.length}</span></div>}
            {mapData.metrics && <div className="sim-stat-item"><Network size={14} /><span className="body-small">{lang === 'nl' ? 'Stappen' : 'Steps'}</span><span className="body-small" style={{ fontWeight: 600 }}>{mapData.metrics.total_steps}</span></div>}
          </div>
        )}
        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {lang === 'nl' ? 'Nieuwe Map' : 'New Map'}</button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          {mapData?.steps && (
            <div className="sim-timeline" style={{ margin: '1.25rem 0 0' }} data-testid="process-steps">
              <h4 className="body-small" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>{lang === 'nl' ? 'Proces Flow' : 'Process Flow'}</h4>
              <div className="sim-timeline-steps">
                {(Array.isArray(mapData.steps) ? mapData.steps : []).map((s, i) => {
                  const isBottleneck = mapData.bottlenecks?.some(b => b.step_id === s.id);
                  return (
                    <div key={i} className="sim-timeline-step">
                      <div className="sim-timeline-dot" style={isBottleneck ? { background: '#c0392b' } : {}} />
                      <div>
                        <span className="body-small" style={{ fontWeight: 600 }}>{s.id}. {s.name} {isBottleneck && <AlertTriangle size={12} style={{ color: '#c0392b', display: 'inline' }} />}</span>
                        <span className="caption text-secondary">{s.owner && `${lang === 'nl' ? 'Eigenaar' : 'Owner'}: ${s.owner}`} {s.duration && `· ${s.duration}`} · {s.type}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="compass-chat-messages" data-testid="mapper-messages">
            {loading && messages.length === 0 && <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Proces mappen...' : 'Mapping process...'}</h3></div>}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Mappen...' : 'Mapping...'}</div></div>}
            <div ref={chatEndRef} />
          </div>
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="mapper-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ProcessMapper;
